import { InspectionModel } from '../../data/types';

/** The slice of the server sync response the success-application needs. */
export interface SyncSuccessItems {
  inspections?: Array<{
    externalId?: string | null;
    id?: number;
    completed?: boolean | null;
  }> | null;
  inspectionCategoryTypes?: Array<{
    id?: number;
    inspectionId?: number;
  }> | null;
  observations?: Array<{
    id?: number;
    externalId?: string | null;
    categoryTypeId?: number;
  }> | null;
}

/**
 * Apply a successful sync POST's response to the copy that was sent. The Inspection
 * record service takes the server ids from it, and applies "sent" only to the parts of
 * the current record that still match the sent copy (#11590).
 *
 * The load-bearing property (pinned by applySyncSuccess.test.ts): EVERY observation
 * of the sent copy is marked isSynced: true - including image observations whose files
 * are still to upload (fileReceived stays false; the image tail is tracked separately).
 * The submit screen's skip-path re-POSTs whenever ANY observation is dirty (the deep
 * InspectionModelHelpers.isSynced check), so after a successful POST nothing sent is
 * dirty, and only image files and changes made during the send remain.
 */
export function applySyncSuccess(
  initialInspection: InspectionModel,
  syncItems: SyncSuccessItems | undefined,
  externalId: string,
): InspectionModel {
  const savedInspection = syncItems?.inspections?.find(
    (i) => i.externalId === externalId,
  );
  const savedCategoryType = syncItems?.inspectionCategoryTypes?.find(
    (c) => c.inspectionId === savedInspection?.id,
  );

  const updated = { ...initialInspection };
  updated.isSynced = true;
  updated.reopened = false;
  updated.completed = savedInspection?.completed ?? updated.completed;
  updated.id = savedInspection?.id ?? updated.id;

  // The response names one category per Inspection, so only the first takes its id;
  // every category was sent, so every one is marked sent (#11590).
  updated.inspectionCategoryTypes = updated.inspectionCategoryTypes.map(
    (categoryType, index) => ({
      ...categoryType,
      id:
        index === 0
          ? (savedCategoryType?.id ?? categoryType.id)
          : categoryType.id,
      isSynced: true,
      observations: categoryType.observations.map((obs) => ({
        ...obs,
        isSynced: true,
        id:
          syncItems?.observations?.find((o) => o.externalId === obs.externalId)
            ?.id ?? obs.id,
      })),
    }),
  );

  return updated;
}
